import React, { useState } from 'react';
import { X, Bug, AlertTriangle, Upload, Image, Trash2, ShieldAlert } from 'lucide-react';
import { showSuccess, showWarning } from '../utils/toast';

const BugReportModal = ({ isOpen, onClose, user = null }) => {
  const [bugReportForm, setBugReportForm] = useState({
    name: user?.email?.split('@')[0] || '',
    email: user?.email || '',
    bugType: '',
    description: '',
    customBugType: '',
    screenshot: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  const handleScreenshotUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showWarning('Please upload an image file (PNG, JPG, etc.)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showWarning('Image file must be smaller than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        setBugReportForm(prev => ({
          ...prev,
          screenshot: base64String
        }));
        setScreenshotPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setBugReportForm(prev => ({ ...prev, screenshot: null }));
    setScreenshotPreview(null);
  };

  const predefinedBugTypes = [
    'Login/Authentication Issue',
    'Subscription not saving',
    'Charts not loading',
    'Mobile display issue',
    'Payment/Billing problem',
    'Data sync issue',
    'Performance/Speed problem',
    'Feature not working',
    'UI/UX Issue',
    'Export/Import Issue',
    'Other (specify below)'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bug-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          ...bugReportForm,
          bugType: bugReportForm.bugType === 'Other (specify below)' ? bugReportForm.customBugType : bugReportForm.bugType,
          user_id: user?.id || null
        })
      });

      if (response.ok) {
        showSuccess('Bug report received! Our engineers are on it.');
        setBugReportForm({
          name: user?.email?.split('@')[0] || '',
          email: user?.email || '',
          bugType: '',
          description: '',
          customBugType: '',
          screenshot: null
        });
        setScreenshotPreview(null);
        onClose();
      }
    } catch (error) {
      console.error('Error submitting bug report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4">
      <div className="bg-neutral-0 rounded-card p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-canvas-lg animate-in fade-in zoom-in-95 duration-300 relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-error/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />

        <div className="flex justify-between items-center mb-10 relative z-10">
          <div className="flex items-center space-x-3">
            <div className="bg-error/10 p-3 rounded-2xl text-error">
              <Bug size={32} />
            </div>
            <div>
              <h2 className="text-3xl font-black text-neutral-900 leading-none tracking-tight uppercase">Bug Hunter.</h2>
              <p className="text-neutral-400 font-bold uppercase tracking-widest text-[10px] mt-1">Found something broken? We're on it.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-neutral-400 hover:bg-neutral-50 rounded-xl transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-1">Name</label>
              <input
                type="text"
                required
                value={bugReportForm.name}
                onChange={(e) => setBugReportForm({ ...bugReportForm, name: e.target.value })}
                className="w-full px-5 py-3 bg-neutral-50 border border-neutral-100 rounded-xl font-bold text-neutral-800 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
                placeholder="Explorer"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-1">Email</label>
              <input
                type="email"
                required
                value={bugReportForm.email}
                onChange={(e) => setBugReportForm({ ...bugReportForm, email: e.target.value })}
                className="w-full px-5 py-3 bg-neutral-50 border border-neutral-100 rounded-xl font-bold text-neutral-800 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-1">Bug Category</label>
            <select
              required
              value={bugReportForm.bugType}
              onChange={(e) => setBugReportForm({ ...bugReportForm, bugType: e.target.value })}
              className="w-full px-5 py-3 bg-neutral-50 border border-neutral-100 rounded-xl font-bold text-neutral-800 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all appearance-none"
            >
              <option value="">Choose a category...</option>
              {predefinedBugTypes.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {bugReportForm.bugType === 'Other (specify below)' && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-1">Specify Topic</label>
              <input
                type="text"
                required
                value={bugReportForm.customBugType}
                onChange={(e) => setBugReportForm({ ...bugReportForm, customBugType: e.target.value })}
                className="w-full px-5 py-3 bg-neutral-50 border border-neutral-100 rounded-xl font-bold text-neutral-800 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-1">Detailed Description</label>
            <textarea
              required
              rows={4}
              value={bugReportForm.description}
              onChange={(e) => setBugReportForm({ ...bugReportForm, description: e.target.value })}
              className="w-full px-5 py-3 bg-neutral-50 border border-neutral-100 rounded-xl font-bold text-neutral-800 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all resize-none"
              placeholder="What happened? Steps to reproduce..."
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-1">Evidence (Optional)</label>
            {!screenshotPreview ? (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-neutral-100 rounded-xl bg-neutral-50/50 hover:bg-neutral-50 hover:border-primary-200 transition-all cursor-pointer group">
                <input type="file" className="hidden" accept="image/*" onChange={handleScreenshotUpload} />
                <Upload className="text-neutral-300 group-hover:text-primary-500 mb-2 transition-colors" size={24} />
                <span className="text-xs font-black text-neutral-400 uppercase tracking-widest">Upload Screenshot</span>
              </label>
            ) : (
              <div className="relative group rounded-xl overflow-hidden border border-neutral-100 shadow-soft">
                <img src={screenshotPreview} alt="Bug evidence" className="w-full h-32 object-cover" />
                <button
                  onClick={removeScreenshot}
                  className="absolute top-2 right-2 p-1.5 bg-neutral-900/80 text-neutral-0 rounded-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-5 bg-neutral-900 text-neutral-0 font-black rounded-2xl shadow-lg hover:bg-neutral-800 transition-all transform active:scale-95 flex items-center justify-center space-x-3 disabled:bg-neutral-200"
          >
            {isSubmitting ? (
              <div className="w-6 h-6 border-2 border-neutral-0 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Bug size={20} />
                <span>Dispatch Report</span>
              </>
            )}
          </button>

          <p className="text-center text-[10px] font-black text-neutral-300 uppercase tracking-widest">Our engineers will personally review your report.</p>
        </form>
      </div>
    </div>
  );
};

export default BugReportModal;